import React from 'react';
import clsx from 'clsx';
import {
    XCircle,
    CheckCircle,
    History,
} from 'lucide-react';
import { PfmeaItem } from '../../types';
import { calculateRPN } from '../../services/pfmeaService';

interface ItemDetailSidebarProps {
    selectedItem: PfmeaItem | null;
    isDark: boolean;
    formatDate: (d?: string) => string;
    onClose: () => void;
}

const DetailField = ({ label, value, isDark }: { label: string; value: string; isDark: boolean }) => (
    <div>
        <label className="text-xs text-slate-500 uppercase">{label}</label>
        <p className={isDark ? 'text-white' : 'text-slate-800'}>{value}</p>
    </div>
);

const RatingBox = ({ value, label }: { value: string | number; label: string }) => (
    <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
    </div>
);

const HistorySection = ({ history, isDark, formatDate }: { history?: any[]; isDark: boolean; formatDate: (d?: string) => string }) => (
    <div>
        <h3 className={clsx('font-semibold flex items-center gap-2 mb-3', isDark ? 'text-white' : 'text-slate-800')}><History size={16} /> Change History</h3>
        {history && history.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-auto">
                {history.map((record, i) => (
                    <div key={i} className={clsx('p-3 rounded-lg text-sm', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                        <div className="flex items-center justify-between">
                            <span className="font-medium capitalize">{record.field}</span>
                            <span className="text-xs text-slate-500">{formatDate(record.timestamp)}</span>
                        </div>
                        <div className="mt-1"><span className="text-red-500 line-through">{record.oldValue}</span> → <span className="text-green-500">{record.newValue}</span></div>
                        <div className="mt-1 text-xs text-slate-500">by {record.user}{record.comment && ` • "${record.comment}"`}</div>
                    </div>
                ))}
            </div>
        ) : <p className="text-sm text-slate-500">No changes recorded.</p>}
    </div>
);

const ItemDetailSidebar: React.FC<ItemDetailSidebarProps> = ({ selectedItem, isDark, formatDate, onClose }) => {
    if (!selectedItem) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
            <div className={clsx('w-full max-w-lg h-full overflow-auto', isDark ? 'bg-slate-800' : 'bg-white')}>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>Item Details</h2>
                        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600"><XCircle size={24} /></button>
                    </div>
                    <div className="space-y-4">
                        <DetailField label="Process Step" value={`${selectedItem.processStepNumber} - ${selectedItem.processStepName}`} isDark={isDark} />
                        <DetailField label="Failure Mode" value={selectedItem.failureMode} isDark={isDark} />
                        <DetailField label="Effect" value={selectedItem.failureEffect || selectedItem.failureEffectEnd || ''} isDark={isDark} />
                        <DetailField label="Cause" value={selectedItem.failureCause || ''} isDark={isDark} />
                        <div className="grid grid-cols-4 gap-2 text-center">
                            <RatingBox value={selectedItem.severity} label="Severity" />
                            <RatingBox value={selectedItem.occurrence} label="Occurrence" />
                            <RatingBox value={selectedItem.detection} label="Detection" />
                            <RatingBox value={calculateRPN(selectedItem.severity, selectedItem.occurrence, selectedItem.detection)} label="RPN" />
                        </div>
                        {selectedItem.status === 'approved' && (
                            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <h3 className="font-semibold text-green-700 dark:text-green-300 flex items-center gap-2 mb-2"><CheckCircle size={16} /> Approval Signature</h3>
                                <div className="text-sm space-y-1">
                                    <p><span className="text-slate-500">Approved By:</span> {selectedItem.approvedBy}</p>
                                    <p><span className="text-slate-500">Date:</span> {formatDate(selectedItem.approvedAt?.toString())}</p>
                                </div>
                            </div>
                        )}
                        <HistorySection history={selectedItem.changeHistory} isDark={isDark} formatDate={formatDate} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItemDetailSidebar;
